import React, { useEffect, useState } from 'react';
import { Outlet, Navigate } from 'react-router-dom';
import { onAuthStateChanged } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { auth, db, ensureUserDocAndPersonalOrg } from '../firebase';
import { EcointelLottie } from './EcoIntelLoader';
import { userNeedsPasswordMigration } from '../utils/passwordMigration';

/**
 * After sign-in: require completed profile (Firestore profileComplete !== false),
 * then prompt legacy magic-link users to set a password before entering the app.
 * Unverified email users proceed on the demo/free tier.
 * Legacy users without profileComplete set are treated as complete.
 */
export default function OnboardingGate() {
  const [gate, setGate] = useState('loading');

  useEffect(() => {
    let cancelled = false;
    const unsub = onAuthStateChanged(auth, async (u) => {
      if (!u) {
        if (!cancelled) setGate('loading');
        return;
      }
      try {
        await u.reload();
      } catch (_) {}
      try {
        let snap = await getDoc(doc(db, 'users', u.uid));
        for (let i = 0; i < 6 && !snap.exists(); i++) {
          await new Promise((r) => setTimeout(r, 350));
          snap = await getDoc(doc(db, 'users', u.uid));
        }
        // Self-heal: if the doc is still missing after the retry window, the
        // first-sign-in write never landed (link-scanner pre-fetch, tab closed
        // before sync, token race, storage eviction — see EmailLinkCallback /
        // firebase.js mitigations). Recreate it client-side now. This is the
        // recovery path for users who already hit the bug before the Auth
        // onCreate backstop existed; ensureUserDocAndPersonalOrg is idempotent
        // and no-ops if the backstop or another tab won the race.
        if (!snap.exists()) {
          try {
            await ensureUserDocAndPersonalOrg(u);
            snap = await getDoc(doc(db, 'users', u.uid));
          } catch (healErr) {
            console.error('OnboardingGate self-heal failed:', healErr?.code, healErr?.message || healErr);
          }
        }
        const d = snap.data();
        if (d && d.profileComplete === false) {
          if (!cancelled) setGate('profile');
          return;
        }
        if (userNeedsPasswordMigration(u, d)) {
          if (!cancelled) setGate('password');
          return;
        }
      } catch (_) {
        if (!cancelled) setGate('ok');
        return;
      }
      if (!cancelled) setGate('ok');
    });
    return () => {
      cancelled = true;
      unsub();
    };
  }, []);

  if (gate === 'loading') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <EcointelLottie message="Checking your account..." lottiePixelWidth={140} lottiePixelHeight={175} />
      </div>
    );
  }
  if (gate === 'profile') return <Navigate to="/complete-profile" replace />;
  if (gate === 'password') return <Navigate to="/set-password" replace />;
  return <Outlet />;
}
